import React, { memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';

export interface ICareerProgress {
  completed: number;
  total: number;
  /** 0–100, already clamped by the transform layer. */
  percent: number;
  className?: string;
}

/**
 * Overall Career Center progress — "3 / 8 lessons" plus a filled bar.
 * Purely presentational: the numbers come from `myCareerCenter`, never counted
 * client-side from the lesson list (spec 0018 §4).
 */
export const CareerProgress = memo(({ completed, total, percent, className }: ICareerProgress) => {
  const { t } = useTranslation();

  return (
    <Wrapper className={className}>
      <ProgressHeader>
        <ProgressLabel>{t('careerCenter.progress.label')}</ProgressLabel>
        <ProgressValue>
          {completed} / {total}
        </ProgressValue>
      </ProgressHeader>
      <Track
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('careerCenter.progress.label')}>
        <Fill style={{ width: `${percent}%` }} />
      </Track>
      <ProgressPercent>{t('careerCenter.progress.percent', { percent })}</ProgressPercent>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  width: 100%;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ProgressLabel = styled.span`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}
`;

const ProgressValue = styled.span`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const Track = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 8px;
  background-color: ${Colors.system_gray};
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 8px;
  background-color: ${Colors.main};
  transition: width 0.3s ease;
`;

const ProgressPercent = styled.div`
  margin-top: 8px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;
